import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle2, Circle, PlayCircle, Award, ChevronLeft, ChevronRight, Loader2,
} from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { VideoPlayer } from '@/components/treinamento/VideoPlayer';
import { treinamentoAulaService, treinamentoCursoService } from '@/services/treinamentosApi';
import { TreinamentoCurso, TreinamentoAula, TreinamentoModulo } from '@/types';

function fmtDuracao(seg?: number) {
  if (!seg) return '';
  const m = Math.floor(seg / 60);
  const s = seg % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function AssistirCurso() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [curso, setCurso] = useState<TreinamentoCurso | null>(null);
  const [loading, setLoading] = useState(true);
  const [aulaId, setAulaId] = useState<string | null>(null);
  const [playUrl, setPlayUrl] = useState<string | null>(null);
  const [loadingVideo, setLoadingVideo] = useState(false);

  // lista plana de aulas (na ordem dos módulos) p/ navegação anterior/próxima
  const aulasFlat = useMemo(() => {
    if (!curso?.modulos) return [] as TreinamentoAula[];
    return curso.modulos.flatMap((m) => m.aulas ?? []);
  }, [curso]);

  const aulaAtual = aulasFlat.find((a) => a.id === aulaId) ?? null;
  const idxAtual = aulasFlat.findIndex((a) => a.id === aulaId);

  const carregarCurso = useCallback(async () => {
    if (!id) return;
    try {
      const data = await treinamentoCursoService.getById(id);
      setCurso(data);
      // seleciona a primeira aula não concluída com vídeo, ou a primeira com vídeo
      const todas = (data.modulos ?? []).flatMap((m) => m.aulas ?? []);
      const proxima = todas.find((a) => a.tem_video && !a.progresso?.concluido) ?? todas.find((a) => a.tem_video);
      if (proxima) setAulaId(proxima.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao carregar curso');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    carregarCurso();
  }, [carregarCurso]);

  // carrega a URL assinada do vídeo ao trocar de aula
  useEffect(() => {
    if (!aulaId || !aulaAtual?.tem_video) {
      setPlayUrl(null);
      return;
    }
    let cancelado = false;
    setLoadingVideo(true);
    setPlayUrl(null);
    treinamentoAulaService
      .playUrl(aulaId)
      .then((r) => {
        if (!cancelado) setPlayUrl(r.url);
      })
      .catch((e) => {
        if (!cancelado) toast.error(e instanceof Error ? e.message : 'Erro ao carregar vídeo');
      })
      .finally(() => {
        if (!cancelado) setLoadingVideo(false);
      });
    return () => {
      cancelado = true;
    };
  }, [aulaId, aulaAtual?.tem_video]);

  const salvarProgresso = useCallback(
    (segundos: number) => {
      if (!aulaId) return;
      treinamentoAulaService.salvarProgresso(aulaId, { posicao_segundos: segundos }).catch(() => {});
    },
    [aulaId],
  );

  const marcarConcluida = useCallback(
    async (avancar: boolean) => {
      if (!aulaId) return;
      try {
        const res = await treinamentoAulaService.concluir(aulaId);
        // atualiza estado local
        setCurso((prev) => {
          if (!prev) return prev;
          const modulos = prev.modulos?.map((m) => ({
            ...m,
            aulas: m.aulas?.map((a) =>
              a.id === aulaId ? { ...a, progresso: { ...(a.progresso ?? { posicao_segundos: 0 }), concluido: true } } : a,
            ),
          }));
          const total = (modulos ?? []).flatMap((m) => m.aulas ?? []).length;
          const concluidas = (modulos ?? [])
            .flatMap((m) => m.aulas ?? [])
            .filter((a) => a.progresso?.concluido).length;
          return {
            ...prev,
            modulos,
            progresso: { total, concluidas, percent: total ? Math.round((concluidas / total) * 100) : 0 },
            certificado: res.certificado ?? prev.certificado,
          };
        });
        if (res.certificado) {
          toast.success('🎉 Curso concluído! Certificado emitido.');
        }
        if (avancar) irPara(idxAtual + 1);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Erro ao concluir aula');
      }
    },
    [aulaId, idxAtual], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const irPara = (idx: number) => {
    const a = aulasFlat[idx];
    if (a) setAulaId(a.id);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
          <Skeleton className="aspect-video w-full rounded-lg" />
          <Skeleton className="h-96 w-full rounded-lg" />
        </div>
      </AppLayout>
    );
  }

  if (!curso) {
    return (
      <AppLayout>
        <div>
          <Button variant="ghost" onClick={() => navigate('/treinamentos')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
          </Button>
          <p className="mt-4 text-muted-foreground">Curso não encontrado.</p>
        </div>
      </AppLayout>
    );
  }

  const pct = curso.progresso?.percent ?? 0;

  return (
    <AppLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/treinamentos')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Treinamentos
          </Button>
          {curso.certificado && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/treinamentos/curso/${curso.id}/certificado`)}>
              <Award className="mr-2 h-4 w-4 text-amber-500" /> Certificado {curso.certificado.codigo}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
          {/* Coluna principal: player */}
          <div className="space-y-4">
            <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
              {loadingVideo ? (
                <div className="flex h-full items-center justify-center text-white/70">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : playUrl && aulaAtual ? (
                <VideoPlayer
                  src={playUrl}
                  title={aulaAtual.titulo}
                  startTime={aulaAtual.progresso?.posicao_segundos ?? 0}
                  onProgress={salvarProgresso}
                  onEnded={() => marcarConcluida(true)}
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-2 text-white/60">
                  <PlayCircle className="h-10 w-10" />
                  <span className="text-sm">Esta aula ainda não tem vídeo.</span>
                </div>
              )}
            </div>

            <div className="rounded-lg border bg-card p-4 flex items-start justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold">{aulaAtual?.titulo ?? curso.titulo}</h1>
                {aulaAtual?.descricao && (
                  <p className="mt-1 text-sm text-muted-foreground">{aulaAtual.descricao}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 border-t pt-4">
              <Button variant="outline" size="sm" disabled={idxAtual <= 0} onClick={() => irPara(idxAtual - 1)}>
                <ChevronLeft className="mr-1 h-4 w-4" /> Anterior
              </Button>
              <Button
                size="sm"
                variant={aulaAtual?.progresso?.concluido ? 'outline' : 'default'}
                onClick={() => marcarConcluida(false)}
                disabled={!aulaAtual}
              >
                <CheckCircle2 className="mr-1 h-4 w-4" />
                {aulaAtual?.progresso?.concluido ? 'Concluída' : 'Marcar como concluída'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={idxAtual >= aulasFlat.length - 1}
                onClick={() => irPara(idxAtual + 1)}
              >
                Próxima <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Coluna lateral: conteúdo do curso */}
          <aside className="overflow-hidden rounded-lg border bg-card">
            {curso.capa_url && (
              <div className="h-28 w-full overflow-hidden">
                <img src={curso.capa_url} alt={curso.titulo} className="h-full w-full object-cover" />
              </div>
            )}
            <div className="space-y-2 border-b p-4">
              <h2 className="font-semibold">{curso.titulo}</h2>
              <Progress value={pct} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {curso.progresso?.concluidas ?? 0} de {curso.progresso?.total ?? 0} aulas • {pct}%
              </p>
            </div>
            <Accordion
              type="multiple"
              defaultValue={(curso.modulos ?? []).map((m) => m.id)}
              className="max-h-[70vh] overflow-y-auto"
            >
              {(curso.modulos ?? []).map((mod: TreinamentoModulo) => (
                <AccordionItem key={mod.id} value={mod.id} className="border-b last:border-b-0">
                  <AccordionTrigger className="px-4 text-sm hover:no-underline">{mod.titulo}</AccordionTrigger>
                  <AccordionContent className="pb-0">
                    {(mod.aulas ?? []).map((aula) => {
                      const ativa = aula.id === aulaId;
                      const concluida = aula.progresso?.concluido;
                      return (
                        <button
                          key={aula.id}
                          onClick={() => setAulaId(aula.id)}
                          className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-muted/60 ${
                            ativa ? 'bg-primary/10 font-medium text-primary' : ''
                          }`}
                        >
                          {concluida ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                          ) : ativa ? (
                            <PlayCircle className="h-4 w-4 shrink-0 text-primary" />
                          ) : (
                            <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                          )}
                          <span className="line-clamp-2 flex-1">{aula.titulo}</span>
                          {aula.duracao_segundos > 0 && (
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {fmtDuracao(aula.duracao_segundos)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </aside>
        </div>
      </div>
    </AppLayout>
  );
}
